import { Injectable } from '@angular/core';
import { ApiService } from '../../../shared/services/api.service';
import { IDataCatalogPagination, IDatasetBladeView, IDatasetCreate, IDatasetFile } from '../models/Dataset';
import { Observable } from 'rxjs/internal/Observable';

@Injectable({
  providedIn: 'root',
})
export class DatasetService {
  constructor(private apiService: ApiService) {}
  readonly datasetRoot = 'api/datasets';

  public uploadDatasetFile(datasetId: number, formData: FormData) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/files/`);
    return this.apiService.post<{ id: number }>(fullAPIUrl, formData, { withCredentials: true });
  }

  public getDatasetDetail(datasetId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/`);
    return this.apiService.get<IDatasetFile>(fullAPIUrl, { withCredentials: true });
  }

  public getDatasetFiles(projectId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(
      this.datasetRoot,
      `?owning_project=${projectId}&assigned_projects=${projectId}`
    );
    return this.apiService.get<IDatasetFile[]>(fullAPIUrl, { withCredentials: true });
  }

  public postDataset(dataset: IDatasetCreate) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, '');
    return this.apiService.post(fullAPIUrl, dataset, { withCredentials: true });
  }

  public getDatasetFileDetail(datasetId: number, fileId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/files/${fileId}/`);
    return this.apiService.get<{ status: string }>(fullAPIUrl, { withCredentials: true });
  }

  public patchDataset(datasetId: number, description: string) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/`);
    return this.apiService.patch(fullAPIUrl, { description }, { withCredentials: true });
  }

  public postGrantDatasetAccess(datasetId: number, project_id: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/project-assignments/`);
    return this.apiService.post(fullAPIUrl, { project_id: project_id }, { withCredentials: true });
  }

  public getServiceAccountKeyfile(datasetId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/download_service_account_keyfile/`);
    return this.apiService.get(fullAPIUrl, { withCredentials: true });
  }

  public getDatasets(params: {
    metadata_tag_match_partial?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
    pagination?: boolean;
    [key: string]: string | number | boolean | readonly (string | number | boolean)[] | undefined;
  }): Observable<IDataCatalogPagination> {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, '');
    // Filter out undefined values for HttpParams compatibility
    const cleanParams: { [param: string]: string | number | boolean | readonly (string | number | boolean)[] } = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanParams[key] = value;
      }
    });
    return this.apiService.get<IDataCatalogPagination>(fullAPIUrl, {
      withCredentials: true,
      params: cleanParams,
    });
  }

  getDatasetBladeView(datasetId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/blade_view/`);
    return this.apiService.get<IDatasetBladeView>(fullAPIUrl, { withCredentials: true });
  }

  getAndSelectDataset(datasetId: number) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/`);
    return this.apiService.get<IDatasetFile>(fullAPIUrl, { withCredentials: true });
  }

  postDatasetRequestAccess(datasetId: number, project_id: number, message: string) {
    const fullAPIUrl = this.apiService.buildFullURL(this.datasetRoot, `${datasetId}/request-access/`);
    return this.apiService.post(fullAPIUrl, { project_id, message }, { withCredentials: true });
  }
}
